import React, { useState, useEffect } from 'react';
import { 
  TrendingUp, TrendingDown, Search, 
  BarChart2, ArrowUp, ArrowDown, AlertTriangle,
  DollarSign, Activity, PieChart, Clock, ArrowUpDown,
  Zap, Globe, Shield, RefreshCw
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { formatCurrency } from '../utils/currencyUtils';
import type { ExchangeRates } from '../utils/currencyUtils';
import { MarketOverview } from '../components/MarketOverview';
import { TrendingSection } from '../components/TrendingSection';
import { SearchBar } from '../components/SearchBar';
import { CryptoDropdown } from '../components/markets/CryptoDropdown';
import { fetchMarketData } from '../utils/api/coingecko';
import { Loader } from '../components/common/Loader';

interface MarketsProps {
  currency: string;
  exchangeRates: ExchangeRates;
}

export function Markets({ currency, exchangeRates }: MarketsProps) {
  const [markets, setMarkets] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMarket, setSelectedMarket] = useState<string | null>(null);
  const [nextRefresh, setNextRefresh] = useState(30000); // 30 seconds
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: 'asc' | 'desc';
  }>({
    key: 'market_cap_rank',
    direction: 'asc'
  });

  const fetchData = React.useCallback(async (showToast = false) => {
    try {
      setIsRefreshing(true);
      const data = await fetchMarketData(true);
      if (Array.isArray(data) && data.length > 0) {
        setMarkets(data);
        setError(null);
      } else {
        throw new Error('Invalid market data received');
      }
    } catch (err: any) {
      console.warn('Market data fetch error:', err);
      setError('Unable to fetch real-time market data. Using cached data.');
    } finally {
      setIsRefreshing(false);
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const timer = setInterval(() => {
      setNextRefresh(prev => {
        if (prev <= 1000) {
          fetchData();
          return 30000;
        }
        return prev - 1000;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [fetchData]);

  const handleSort = (key: string) => {
    setSortConfig(current => ({
      key,
      direction: current.key === key && current.direction === 'desc' ? 'asc' : 'desc'
    }));
  };

  const filteredMarkets = React.useMemo(() => {
    let result = markets.filter(market => 
      market.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      market.symbol.toLowerCase().includes(searchTerm.toLowerCase())
    );

    result.sort((a, b) => {
      const multiplier = sortConfig.direction === 'asc' ? 1 : -1;
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return aValue.localeCompare(bValue) * multiplier;
      }
      return ((aValue as number) - (bValue as number)) * multiplier;
    });

    return result;
  }, [markets, searchTerm, sortConfig]);

  if (isLoading) {
    return <Loader />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50/20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Market Overview */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <MarketOverview currency={currency} exchangeRates={exchangeRates} />
        </motion.div>

        {/* Trending Sections */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 my-8">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <TrendingSection type="coins" currency={currency} exchangeRates={exchangeRates} />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <TrendingSection type="dex" currency={currency} exchangeRates={exchangeRates} />
          </motion.div>
        </div>

        {/* Market Table Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden"
        >
          {/* Table Header */}
          <div className="p-6 border-b border-gray-100">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                  <BarChart2 className="w-6 h-6 text-blue-500" />
                  Cryptocurrency Markets
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  Real-time prices and market data
                </p>
              </div>

              <div className="flex items-center gap-4">
                <div className="text-sm text-gray-500 flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  Next update in {Math.ceil(nextRefresh / 1000)}s
                </div>
                <button
                  onClick={() => fetchData(true)}
                  disabled={isRefreshing}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                  Refresh
                </button>
              </div>
            </div>

            <div className="mt-6 flex flex-col md:flex-row md:items-center justify-between gap-4">
              <SearchBar 
                value={searchTerm}
                onChange={setSearchTerm}
                placeholder="Search by name or symbol..."
              />
            </div>
          </div>

          {error && (
            <div className="p-4 bg-amber-50 border-b border-amber-100">
              <div className="flex items-center gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0" />
                <p className="text-sm text-amber-700">{error}</p>
              </div>
            </div>
          )}

          {/* Market Table */}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50/50">
                <tr>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                    onClick={() => handleSort('market_cap_rank')}
                  >
                    <div className="flex items-center gap-2">
                      <Globe className="w-4 h-4" />
                      Rank {sortConfig.key === 'market_cap_rank' && (
                        sortConfig.direction === 'asc' ? 
                          <ArrowUp className="w-4 h-4" /> : 
                          <ArrowDown className="w-4 h-4" />
                      )}
                    </div>
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                    onClick={() => handleSort('name')}
                  >
                    <div className="flex items-center gap-2">
                      <Shield className="w-4 h-4" />
                      Asset {sortConfig.key === 'name' && (
                        sortConfig.direction === 'asc' ? 
                          <ArrowUp className="w-4 h-4" /> : 
                          <ArrowDown className="w-4 h-4" />
                      )}
                    </div>
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                    onClick={() => handleSort('current_price')}
                  >
                    <div className="flex items-center gap-2">
                      <DollarSign className="w-4 h-4" />
                      Price {sortConfig.key === 'current_price' && (
                        sortConfig.direction === 'asc' ? 
                          <ArrowUp className="w-4 h-4" /> : 
                          <ArrowDown className="w-4 h-4" />
                      )}
                    </div>
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                    onClick={() => handleSort('price_change_percentage_24h')}
                  >
                    <div className="flex items-center gap-2">
                      <Activity className="w-4 h-4" />
                      24h Change {sortConfig.key === 'price_change_percentage_24h' && (
                        sortConfig.direction === 'asc' ? 
                          <ArrowUp className="w-4 h-4" /> : 
                          <ArrowDown className="w-4 h-4" />
                      )}
                    </div>
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                    onClick={() => handleSort('market_cap')}
                  >
                    <div className="flex items-center gap-2">
                      <PieChart className="w-4 h-4" />
                      Market Cap {sortConfig.key === 'market_cap' && (
                        sortConfig.direction === 'asc' ? 
                          <ArrowUp className="w-4 h-4" /> : 
                          <ArrowDown className="w-4 h-4" />
                      )}
                    </div>
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                    onClick={() => handleSort('total_volume')}
                  >
                    <div className="flex items-center gap-2">
                      <Zap className="w-4 h-4" />
                      Volume (24h) {sortConfig.key === 'total_volume' && (
                        sortConfig.direction === 'asc' ? 
                          <ArrowUp className="w-4 h-4" /> : 
                          <ArrowDown className="w-4 h-4" />
                      )}
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredMarkets.map((market) => (
                  <React.Fragment key={market.id}>
                    <motion.tr
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      onClick={() => setSelectedMarket(selectedMarket === market.id ? null : market.id)}
                      className={`hover:bg-gray-50 transition-colors cursor-pointer ${
                        selectedMarket === market.id ? 'bg-blue-50/50' : ''
                      }`}
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        #{market.market_cap_rank}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <img
                            src={market.image}
                            alt={market.name}
                            className="w-8 h-8 rounded-full"
                          />
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {market.name}
                            </div>
                            <div className="text-sm text-gray-500 uppercase">
                              {market.symbol}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {formatCurrency(market.current_price, currency, exchangeRates)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className={`text-sm flex items-center gap-1 font-medium ${
                          market.price_change_percentage_24h >= 0 
                            ? 'text-green-600' 
                            : 'text-red-600'
                        }`}>
                          {market.price_change_percentage_24h >= 0 ? (
                            <TrendingUp className="w-4 h-4" />
                          ) : (
                            <TrendingDown className="w-4 h-4" />
                          )}
                          {Math.abs(market.price_change_percentage_24h).toFixed(2)}%
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {formatCurrency(market.market_cap, currency, exchangeRates)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {formatCurrency(market.total_volume, currency, exchangeRates)}
                        </div>
                      </td>
                    </motion.tr>
                    {selectedMarket === market.id && (
                      <tr>
                        <td colSpan={6}>
                          <CryptoDropdown
                            isOpen={true}
                            market={market}
                            currency={currency}
                            exchangeRates={exchangeRates}
                            onClose={() => setSelectedMarket(null)}
                          />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>
      </div>
    </div>
  );
}